import { useEffect, useRef, ReactNode } from 'react';

interface AccessibleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Must match the `id` of the heading element inside the dialog */
  labelledById: string;
  children: ReactNode;
  /** Classes for the white inner panel (background, sizing, etc.) */
  className?: string;
  /** Classes for the fixed backdrop overlay — use to control z-index stacking */
  backdropClassName?: string;
}

const FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AccessibleDialog({
  isOpen,
  onClose,
  labelledById,
  children,
  className = '',
  backdropClassName = 'fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4',
}: AccessibleDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Save the previously focused element and focus into dialog on open;
  // restore focus when dialog closes.
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
    focusable?.[0]?.focus();
    return () => {
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  // Escape key to close; Tab key to trap focus within dialog.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS) ?? []
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={backdropClassName}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className={className}
      >
        {children}
      </div>
    </div>
  );
}
